import { useEffect, useState } from 'react'
import api from '../services/api'
import { X, Zap, Bug, Shield, Wrench, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react'

const TYPE_ICON: Record<string, any> = {
  feature: Zap, fix: Bug, security: Shield, maintenance: Wrench,
}
const TYPE_COLOR: Record<string, string> = {
  feature: '#0066FF', fix: '#F5921E', security: '#EF4444', maintenance: '#8B949E',
}
const SEVERITY_STYLE: Record<string, { border: string; bg: string; badge: string }> = {
  normal:    { border: '#2A3441', bg: '#161B22', badge: '#8B949E' },
  important: { border: '#F5921E55', bg: '#1C1408', badge: '#F5921E' },
  critical:  { border: '#EF444455', bg: '#1C0808', badge: '#EF4444' },
}

type Update = {
  id: string; version: string; title: string; body: string
  type: string; severity: string; requires_ack: number; queue_id: string
}

export default function UpdateNotification() {
  const [updates, setUpdates] = useState<Update[]>([])
  const [expanded, setExpanded] = useState<string | null>(null)
  const [acknowledging, setAcknowledging] = useState<string | null>(null)
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  useEffect(() => {
    fetchPending()
    // Poll every 5 minutes
    const timer = setInterval(fetchPending, 300_000)
    return () => clearInterval(timer)
  }, [])

  async function fetchPending() {
    try {
      const r = await api.get('/platform-updates/pending')
      setUpdates(r.data.data || [])
    } catch {}
  }

  async function acknowledge(update: Update) {
    setAcknowledging(update.id)
    try {
      await api.post(`/platform-updates/${update.id}/acknowledge`)
      setUpdates(u => u.filter(x => x.id !== update.id))
    } catch {}
    setAcknowledging(null)
  }

  function dismiss(id: string) {
    setDismissed(d => new Set([...d, id]))
  }

  const visible = updates.filter(u => !dismissed.has(u.id))
  if (visible.length === 0) return null

  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem',
      zIndex: 9000, display: 'flex', flexDirection: 'column-reverse', gap: '0.75rem',
      maxWidth: 420, width: '100%',
    }}>
      {visible.map((u, idx) => {
        if (idx > 2) return null // max 3 stacked
        const Icon = TYPE_ICON[u.type] || Zap
        const color = TYPE_COLOR[u.type] || '#0066FF'
        const style = SEVERITY_STYLE[u.severity] || SEVERITY_STYLE.normal
        const isExpanded = expanded === u.id

        return (
          <div key={u.id} style={{
            background: style.bg,
            border: `1px solid ${style.border}`,
            borderRadius: 14, overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            fontFamily: "'Inter', sans-serif",
          }}>
            {/* Header */}
            <div style={{ padding: '0.875rem 1rem', display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <Icon size={16} color={color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <span style={{ color: '#E6EDF3', fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {u.title}
                  </span>
                  <span style={{ background: `${style.badge}22`, color: style.badge, fontSize: '0.6875rem', fontWeight: 700, padding: '1px 6px', borderRadius: 99, textTransform: 'uppercase', flexShrink: 0 }}>
                    {u.severity}
                  </span>
                </div>
                <div style={{ color: '#8B949E', fontSize: '0.75rem' }}>
                  Platform update v{u.version}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                <button onClick={() => setExpanded(isExpanded ? null : u.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B949E', padding: 2 }}>
                  {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                </button>
                {!u.requires_ack && (
                  <button onClick={() => dismiss(u.id)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8B949E', padding: 2 }}>
                    <X size={15} />
                  </button>
                )}
              </div>
            </div>

            {/* Expanded body */}
            {isExpanded && (
              <div style={{ padding: '0 1rem 0.875rem', borderTop: `1px solid #2A344155` }}>
                <div style={{ marginTop: '0.75rem', color: '#8B949E', fontSize: '0.8125rem', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                  {u.body}
                </div>
              </div>
            )}

            {/* Acknowledge footer */}
            <div style={{ padding: '0.625rem 1rem', borderTop: `1px solid #2A344133`, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => acknowledge(u)}
                disabled={acknowledging === u.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  background: color, border: 'none', borderRadius: 8,
                  color: '#fff', padding: '0.375rem 0.875rem',
                  cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600,
                  opacity: acknowledging === u.id ? 0.7 : 1,
                }}
              >
                <CheckCircle size={13} />
                {acknowledging === u.id ? 'Confirming…' : 'Got it'}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
